import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Heart, Star, BookOpen } from 'lucide-react';
import { useReadingProgress } from '../hooks/useReadingProgress';

// Otaku-style card - clean, minimal, compact for two-row layout
export function ComicCard({ comic, size = 'normal', compact = false }) {
  const [isLiked, setIsLiked] = useState(false);
  const { getProgress } = useReadingProgress();
  const progress = getProgress(comic.id);

  // Generate star rating (1-5) based on likes
  const rating = comic.likes ? Math.min(5, Math.max(1, Math.round(comic.likes / 200))) : 4;
  
  // Calculate reading progress percentage
  const totalEpisodes = comic.episode_count || 0;
  const readEpisodes = progress?.episodeNumber || 0;
  const progressPercent = totalEpisodes > 0 ? Math.round((readEpisodes / totalEpisodes) * 100) : 0;
  const hasProgress = progress && readEpisodes > 0;

  return (
    <Link 
      to={'/comic/' + comic.id}
      className="group block"
      data-testid={'comic-card-' + comic.id}
    >
      {/* Image Container - Compact */}
      <div className="relative aspect-[3/4] overflow-hidden bg-[var(--surface-highlight)] rounded">
        <img 
          src={comic.cover_image} 
          alt={comic.title}
          className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
        />
        
        {/* Heart Button - Top Right */}
        <button 
          onClick={(e) => { e.preventDefault(); setIsLiked(!isLiked); }}
          className={`absolute top-2 right-2 w-6 h-6 rounded-full flex items-center justify-center transition-all ${
            isLiked 
              ? 'bg-[#E63946] text-white' 
              : 'bg-white/90 text-[var(--text-secondary)] hover:text-[#E63946]'
          }`}
        >
          <Heart size={12} fill={isLiked ? 'currentColor' : 'none'} />
        </button>

        {/* Free Badge - Top Left */}
        {comic.is_free && (
          <span className="absolute top-2 left-2 px-1.5 py-0.5 bg-[#E63946] text-white text-[8px] font-semibold uppercase tracking-wider rounded-sm">
            Free
          </span>
        )}
        
        {/* Reading Progress Bar - Bottom */}
        {hasProgress && (
          <div className="absolute bottom-0 left-0 right-0">
            <div className="h-1 bg-black/50">
              <div 
                className="h-full bg-[#E63946] transition-all"
                style={{ width: `${progressPercent}%` }}
              />
            </div>
          </div>
        )}
      </div>
      
      {/* Content Below Image - Compact */}
      <div className="pt-2 pb-1">
        <h3 className="font-teko text-sm uppercase tracking-wide text-[var(--text-primary)] group-hover:text-[#E63946] transition-colors line-clamp-1">
          {comic.title}
        </h3>
        
        <div className="flex items-center justify-between mt-0.5">
          {/* Episode Count with Progress */}
          {hasProgress ? (
            <span className="flex items-center gap-1 text-[10px] text-[#E63946] font-medium">
              <BookOpen size={10} />
              {readEpisodes}/{totalEpisodes} Ep
            </span>
          ) : (
            <span className="text-[10px] text-[var(--text-secondary)]">
              {totalEpisodes || 3} Ep
            </span>
          )}
          
          {/* Star Rating - Compact */}
          <div className="flex items-center gap-px">
            {[...Array(5)].map((_, i) => (
              <Star 
                key={i} 
                size={8} 
                className={i < rating ? 'text-[#FF6B35] fill-[#FF6B35]' : 'text-[var(--border)]'}
              />
            ))}
          </div>
        </div>
      </div>
    </Link>
  );
}

export function ComicCardSkeleton({ size = 'normal', compact = false }) {
  return (
    <div className="block">
      <div className="aspect-[3/4] bg-[var(--surface-highlight)] animate-pulse rounded" />
      <div className="pt-2 pb-1 space-y-1">
        <div className="h-4 bg-[var(--surface-highlight)] rounded animate-pulse w-3/4" />
        <div className="h-3 bg-[var(--surface-highlight)] rounded animate-pulse w-1/2" />
      </div>
    </div>
  );
}

export default ComicCard;
